/**
 * Published-country registry — single source of truth for which countries
 * have a *fully shipped* wiki guide (hub + 12 chapters × RU + EN).
 *
 * Frontend-friendly mirror of the same data in
 * `web/scripts/published-articles.mjs` (which lists per-country *topics* for
 * sitemap + prerender). The mjs file is the source of truth at build time;
 * this file is the source of truth at run time for nav, footers and
 * cross-page links.
 *
 * **Keep both in sync** when shipping a new country. The lint-prose + tsc
 * gates will not catch a divergence between them; the user-visible nav will.
 *
 * Used by:
 *   - `MarketingHeader` (country-guides dropdown)
 *   - `SiteFooter` (guides column)
 *   - `HomePage` AtlasTeaser (badges + links on the 4 flagship rows)
 *   - `ComparePage`, `DetailsPanel`, `NextStepsPage` (contextual entry points)
 */

import type { CountryCode } from './architecture';

/** Country codes with a fully shipped wiki guide. Order = editorial priority. */
export const PUBLISHED_COUNTRY_CODES: readonly CountryCode[] = [
  'IT',
  'PT',
  'ES',
  'AE',
  'CY',
  'EE',
  'AT',
  'GR',
  'TH',
] as const;

export type PublishedCountryCode = (typeof PUBLISHED_COUNTRY_CODES)[number];

/** True if a country has a fully-shipped wiki guide today. */
export function isPublishedCountry(code: string | undefined | null): code is PublishedCountryCode {
  if (!code) return false;
  return (PUBLISHED_COUNTRY_CODES as readonly string[]).includes(code);
}
